import TipoVoto, { TIPO_VOTO } from '@/dominio/TipoVoto';

class VotacaoService {
    /**
     * Cria uma nova matéria
     */
    static criarNovaMateria(siglaRelator) {
        return {
            descricao: 'Nova matéria',
            deliberacoes: [
                {
                    divergente: false,
                    magistradoResponsavel: siglaRelator,
                    descricao: 'Sentido da deliberação do relator',
                    votos: [
                        {
                            magistradoResponsavel: siglaRelator,
                            tipo: { id: TIPO_VOTO.RELATOR.codigo }
                        }
                    ]
                }
            ]
        };
    }

    /**
     * Cria uma nova deliberação
     */
    static criarNovaDeliberacao(materia, siglaRelator, siglaMagistrado, descricao = '') {
        if (!materia) return null;

        const isRelator = siglaRelator === siglaMagistrado;

        return {
            magistradoResponsavel: siglaMagistrado,
            descricao,
            divergente: false,
            votos: [
                {
                    magistradoResponsavel: siglaMagistrado,
                    tipo: { id: isRelator ? TIPO_VOTO.RELATOR.codigo : TIPO_VOTO.CONVERGENTE.codigo }
                }
            ]
        };
    }

    /**
     * Aplica um voto a uma deliberação
     */
    static aplicarVoto(materia, deliberacao, magistradoSigla, tipoVotoId, materias = []) {
        if (!materia || !deliberacao || !magistradoSigla) {
            return { sucesso: false, erro: 'Parâmetros inválidos' };
        }

        const siglaAutor = deliberacao.magistradoResponsavel;
        const isAutor = magistradoSigla === siglaAutor;

        // Se for o autor da deliberação
        if (isAutor) {
            // Relator pode votar como RELATOR ou CONVERGENTE
            if (magistradoSigla === materia.relatorSigla) {
                const votoValidoRelator =
                    tipoVotoId === TIPO_VOTO.RELATOR.codigo || tipoVotoId === TIPO_VOTO.CONVERGENTE.codigo;

                if (!votoValidoRelator) {
                    return {
                        sucesso: false,
                        erro: 'O relator deve acompanhar sua deliberação como Relator ou Convergente.'
                    };
                }
            } else if (tipoVotoId !== TIPO_VOTO.CONVERGENTE.codigo) {
                // Autores não-relator devem votar como CONVERGENTE
                return {
                    sucesso: false,
                    erro: 'O autor da deliberação deve acompanhá-la totalmente (voto convergente).'
                };
            }
        }

        // Verifica se é voto convergente
        const isVotoConvergente =
            tipoVotoId === TIPO_VOTO.CONVERGENTE.codigo ||
            tipoVotoId === TIPO_VOTO.CONVERGENTE_COM_RESSALVA.codigo ||
            tipoVotoId === TIPO_VOTO.RELATOR.codigo;

        // Verifica se já tem voto convergente em outra deliberação
        if (isVotoConvergente) {
            const jaTemVotoConvergente = VotacaoService.verificarSeMagistradoTemVotoConvergenteNaMateria(
                materias,
                materia.id,
                magistradoSigla,
                deliberacao.id
            );

            if (jaTemVotoConvergente) {
                return {
                    sucesso: false,
                    erro: 'Este magistrado já possui um voto convergente em outra deliberação.'
                };
            }
        }

        // Remove voto atual
        const votosFiltrados = deliberacao.votos?.filter((v) => v.magistradoResponsavel !== magistradoSigla) || [];

        // Adiciona novo voto (se não for null)
        if (tipoVotoId !== null) {
            votosFiltrados.push({
                magistradoResponsavel: magistradoSigla,
                tipo: { id: tipoVotoId }
            });
        }

        return {
            sucesso: true,
            votosAtualizados: votosFiltrados
        };
    }

    /**
     * Verifica se magistrado tem voto convergente na matéria
     */
    static verificarSeMagistradoTemVotoConvergenteNaMateria(materias, materiaId, magistradoSigla, deliberacaoAtualId) {
        const materia = materias.find((t) => t.id === materiaId);
        if (!materia?.deliberacoes) return false;

        return materia.deliberacoes.some((deliberacao) => {
            if (deliberacao.id === deliberacaoAtualId) return false;

            const voto = deliberacao.votos?.find((v) => v.magistradoResponsavel === magistradoSigla);
            if (!voto) return false;

            const tipoVotoId = voto.tipo?.id;
            return (
                tipoVotoId === TIPO_VOTO.CONVERGENTE.codigo ||
                tipoVotoId === TIPO_VOTO.CONVERGENTE_COM_RESSALVA.codigo ||
                tipoVotoId === TIPO_VOTO.RELATOR.codigo
            );
        });
    }

    /**
     * Garante que o autor da deliberação sempre vote nela mesma
     * - Se for o RELATOR: vota como TIPO_VOTO.RELATOR.codigo
     * - Se for outro magistrado: vota como TIPO_VOTO.CONVERGENTE.codigo
     */
    static garantirVotoAutorNaPropriaDeliberacao = (materia, deliberacao, siglaRelator) => {
        if (!materia || !deliberacao?.magistradoResponsavel) {
            return;
        }

        const siglaAutor = deliberacao.magistradoResponsavel;
        const isAutorRelator = siglaAutor === siglaRelator;

        // Verifica se o autor já votou em sua deliberação
        const votoAutor = deliberacao.votos?.find((v) => v.magistradoResponsavel === siglaAutor);

        const tipoVotoEsperado = isAutorRelator ? TIPO_VOTO.RELATOR.codigo : TIPO_VOTO.CONVERGENTE.codigo;

        if (!votoAutor) {
            // Adiciona voto do autor (relator ou convergente)
            if (!deliberacao.votos) {
                deliberacao.votos = [];
            }
            deliberacao.votos.push({
                magistradoResponsavel: siglaAutor,
                tipo: { id: tipoVotoEsperado }
            });
        } else {
            // Se já votou, GARANTE que seja do tipo correto
            const tipoVotoId = votoAutor.tipo?.id;

            if (tipoVotoId !== tipoVotoEsperado) {
                // Corrige para o tipo de voto esperado
                votoAutor.tipo = { id: tipoVotoEsperado };

                if (isAutorRelator) {
                    console.log(`Voto do relator ${siglaAutor} corrigido para RELATOR`);
                } else {
                    console.log(`Voto do autor ${siglaAutor} corrigido para CONVERGENTE`);
                }
            }
        }

        return deliberacao;
    };

    /**
     * Verifica e corrige se todos os autores estão votando em suas próprias deliberações
     */
    static verificarECorrigirVotosAutores = (siglaRelator, materiaEspecifico = null) => {
        const materiasParaVerificar = materiaEspecifico ? [materiaEspecifico] : this.materias;
        let correcoesRealizadas = 0;

        materiasParaVerificar.forEach((materia) => {
            materia.deliberacoes.forEach((deliberacao) => {
                if (deliberacao.magistradoResponsavel) {
                    const siglaAutor = deliberacao.magistradoResponsavel;
                    const votoAutor = deliberacao.votos?.find((v) => v.magistradoResponsavel === siglaAutor);
                    const isRelator = siglaAutor === siglaRelator;
                    const tipoEsperado = isRelator ? TIPO_VOTO.RELATOR.codigo : TIPO_VOTO.CONVERGENTE.codigo;

                    if (!votoAutor || votoAutor.tipo?.id !== tipoEsperado) {
                        // Remove voto atual se existir
                        deliberacao.votos =
                            deliberacao.votos?.filter((v) => v.magistradoResponsavel !== siglaAutor) || [];

                        // Adiciona voto correto
                        deliberacao.votos.push({
                            magistradoResponsavel: siglaAutor,
                            tipo: { id: tipoEsperado }
                        });

                        correcoesRealizadas++;
                    }
                }
            });
        });

        if (correcoesRealizadas > 0) {
            this.mensagemStore.abrirMensagemSucesso(
                `${correcoesRealizadas} autor(es) corrigido(s) para votarem corretamente em suas próprias deliberações`
            );
        }

        return correcoesRealizadas;
    };

    /**
     * Verifica se o voto do autor (relator ou não) é válido
     */
    static isVotoAutorValido = (voto, siglaAutor, isRelator) => {
        if (!voto) return false;

        const tipoId = voto.tipo?.id;

        if (isRelator) {
            // Relator deve votar como RELATOR
            return tipoId === TIPO_VOTO.RELATOR.codigo;
        } else {
            // Autores não-relator devem votar apenas como CONVERGENTE
            return tipoId === TIPO_VOTO.CONVERGENTE.codigo;
        }
    };

    /**
     * Obtém voto padrão baseado no status
     */
    static obterVotoPadraoPeloStatus(magistrado, siglaRelator = null) {
        if (magistrado.relator && siglaRelator === magistrado.siglaDoMagistrado) {
            return TIPO_VOTO.RELATOR.codigo; // Relator vota como RELATOR
        }
        if (magistrado.ausente) return TIPO_VOTO.AUSENTE.codigo;
        if (magistrado.impedido) return TIPO_VOTO.IMPEDIDO.codigo;
        if (magistrado.possivelmenteImpedido) return TIPO_VOTO.IMPEDIDO.codigo;
        return null;
    }

    /**
     * Pré-preenche votos automaticamente
     */
    static preencherVotosAutomaticamente(materia, composicaoComVotos, siglaRelator) {
        if (!materia?.deliberacoes || !composicaoComVotos) return { ...materia };

        const materiaCopia = JSON.parse(JSON.stringify(materia));

        materiaCopia.deliberacoes.forEach((deliberacao) => {
            const isDeliberacaoRelator = deliberacao.magistradoResponsavel === siglaRelator;

            composicaoComVotos.forEach((mag) => {
                const siglaMag = mag.siglaDoMagistrado;
                const isAutor = siglaMag === deliberacao.magistradoResponsavel;

                // Autor vota na própria deliberação
                if (isAutor) {
                    if (isDeliberacaoRelator) {
                        // Relator vota como RELATOR na sua deliberação
                        VotacaoService.adicionarVotoSeNaoExistir(deliberacao, siglaMag, TIPO_VOTO.RELATOR.codigo);
                    } else {
                        // Autores não-relator votam como CONVERGENTE
                        VotacaoService.adicionarVotoSeNaoExistir(deliberacao, siglaMag, TIPO_VOTO.CONVERGENTE.codigo);
                    }
                    return;
                }

                // Pré-preenche baseado no status
                const votoPadrao = VotacaoService.obterVotoPadraoPeloStatus(mag, siglaRelator);
                if (votoPadrao !== null) {
                    VotacaoService.adicionarVotoSeNaoExistir(deliberacao, siglaMag, votoPadrao);
                }
            });
        });

        return materiaCopia;
    }

    /**
     * Adiciona voto se não existir
     */
    static adicionarVotoSeNaoExistir(deliberacao, magistradoSigla, tipoVotoId) {
        const votoExistente = deliberacao.votos?.find((v) => v.magistradoResponsavel === magistradoSigla);
        if (votoExistente) return;

        deliberacao.votos = deliberacao.votos || [];
        deliberacao.votos.push({
            magistradoResponsavel: magistradoSigla,
            tipo: { id: tipoVotoId }
        });
    }

    /**
     * Verifica inconsistências
     */
    static verificarInconsistencias(materias, siglaRelator) {
        const inconsistencias = {
            magistradosMultiplasDeliberacoes: [],
            autoresSemVotoProprio: [],
            autoresComVotoInvalido: [],
            totalInconsistencias: 0
        };

        materias.forEach((materia) => {
            const votosPorMagistrado = new Map();

            materia.deliberacoes?.forEach((deliberacao) => {
                const siglaAutor = deliberacao.magistradoResponsavel;
                if (!siglaAutor) return;

                // Verifica voto do autor
                const votoAutor = deliberacao.votos?.find((v) => v.magistradoResponsavel === siglaAutor);
                const isRelator = siglaAutor === siglaRelator;

                if (!votoAutor) {
                    inconsistencias.autoresSemVotoProprio.push({
                        magistrado: siglaAutor,
                        materia: materia.descricao,
                        deliberacao: deliberacao.descricao || 'Sem descrição',
                        isRelator,
                        tipoEsperado: isRelator ? 'RELATOR' : 'CONVERGENTE'
                    });
                    inconsistencias.totalInconsistencias++;
                } else {
                    const tipoVotoId = votoAutor.tipo?.id;

                    if (isRelator) {
                        // Relator deve votar como RELATOR ou CONVERGENTE
                        if (tipoVotoId !== TIPO_VOTO.RELATOR.codigo && tipoVotoId !== TIPO_VOTO.CONVERGENTE.codigo) {
                            inconsistencias.autoresComVotoInvalido.push({
                                magistrado: siglaAutor,
                                materia: materia.descricao,
                                deliberacao: deliberacao.descricao || 'Sem descrição',
                                isRelator,
                                tipoAtual: TipoVoto.getDescricaoByCodigo(tipoVotoId),
                                tipoEsperado: 'RELATOR ou CONVERGENTE'
                            });
                            inconsistencias.totalInconsistencias++;
                        }
                    } else if (tipoVotoId !== TIPO_VOTO.CONVERGENTE.codigo) {
                        // Autores não-relator devem votar como CONVERGENTE
                        inconsistencias.autoresComVotoInvalido.push({
                            magistrado: siglaAutor,
                            materia: materia.descricao,
                            deliberacao: deliberacao.descricao || 'Sem descrição',
                            isRelator,
                            tipoAtual: TipoVoto.getDescricaoByCodigo(tipoVotoId),
                            tipoEsperado: 'CONVERGENTE'
                        });
                        inconsistencias.totalInconsistencias++;
                    }
                }

                // Coleta votos convergentes
                deliberacao.votos?.forEach((voto) => {
                    const siglaMag = voto.magistradoResponsavel;
                    const tipoVoto = voto.tipo?.id;

                    if (
                        tipoVoto === TIPO_VOTO.CONVERGENTE.codigo ||
                        tipoVoto === TIPO_VOTO.CONVERGENTE_COM_RESSALVA.codigo ||
                        tipoVoto === TIPO_VOTO.RELATOR.codigo
                    ) {
                        if (!votosPorMagistrado.has(siglaMag)) {
                            votosPorMagistrado.set(siglaMag, []);
                        }

                        const tipoDescricao = TipoVoto.getDescricaoByCodigo(tipoVoto);

                        votosPorMagistrado.get(siglaMag).push({
                            deliberacaoId: deliberacao.id,
                            deliberacao: deliberacao.descricao || 'Sem descrição',
                            autor: siglaAutor,
                            tipo: tipoDescricao,
                            magistrado: siglaMag,
                            materia: materia.descricao
                        });
                    }
                });
            });

            // Verifica múltiplas deliberações
            votosPorMagistrado.forEach((votos, siglaMag) => {
                if (votos.length > 1) {
                    const deliberacoesUnicas = [...new Set(votos.map((v) => v.deliberacaoId))];

                    if (deliberacoesUnicas.length > 1) {
                        inconsistencias.magistradosMultiplasDeliberacoes.push({
                            magistrado: siglaMag,
                            materia: materia.descricao,
                            votos: votos,
                            deliberacoes: [...new Set(votos.map((v) => v.deliberacao))],
                            totalVotosConvergentes: votos.length
                        });
                        inconsistencias.totalInconsistencias++;
                    }
                }
            });
        });

        return inconsistencias;
    }

    /**
     * Corrige inconsistências automaticamente
     */
    static corrigirInconsistenciasAutomaticamente(materias, siglaRelator) {
        const materiasCorrigidos = JSON.parse(JSON.stringify(materias));
        let correcoesRealizadas = 0;

        materiasCorrigidos.forEach((materia) => {
            materia.deliberacoes?.forEach((deliberacao) => {
                const siglaAutor = deliberacao.magistradoResponsavel;
                if (!siglaAutor) return;

                const isRelator = siglaAutor === siglaRelator;

                // Garante que o autor vote na própria deliberação
                const votoAutorIndex = deliberacao.votos?.findIndex((v) => v.magistradoResponsavel === siglaAutor);

                if (votoAutorIndex === -1 || votoAutorIndex === undefined) {
                    // Adiciona voto do autor
                    deliberacao.votos = deliberacao.votos || [];
                    deliberacao.votos.push({
                        magistradoResponsavel: siglaAutor,
                        tipo: {
                            id: isRelator ? TIPO_VOTO.RELATOR.codigo : TIPO_VOTO.CONVERGENTE.codigo
                        }
                    });
                    correcoesRealizadas++;
                } else {
                    // Corrige voto existente se necessário
                    const votoAutor = deliberacao.votos[votoAutorIndex];
                    const tipoCorreto = isRelator ? TIPO_VOTO.RELATOR.codigo : TIPO_VOTO.CONVERGENTE.codigo;

                    if (votoAutor.tipo?.id !== tipoCorreto) {
                        votoAutor.tipo = { id: tipoCorreto };
                        correcoesRealizadas++;
                    }
                }
            });
        });

        return {
            materias: materiasCorrigidos,
            correcoesRealizadas
        };
    }
}

export default VotacaoService;
